/**
 * B2BLandingPage — narrative order:
 * Home → Problem → (proof) → Solution → Who it's for → CTA
 * Features have moved to /business/features (B2BPlatformCenter → FeaturesPage)
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { B2BLandingHeader } from './B2BLandingHeader';
import { B2BHero } from './B2BHero';
import { B2BBeforeAfter } from './B2BBeforeAfter';
import { B2BMetricsStrip } from './B2BMetricsStrip';
import { B2BHowItWorks } from './B2BHowItWorks';
import { B2BWorksBestFor } from './B2BWorksBestFor';
import { B2BFinalCTA, B2BFooter } from './B2BTestimonialsAndCTA';

/** Light theme page wash — B2C `light.html`: --bg-secondary #f3f3f3, --bg #ffffff */
const LANDING_PAGE_BG =
  'min-h-screen bg-[#f3f3f3] text-gray-900 transition-colors duration-300 dark:bg-zinc-950 dark:text-white';

export function B2BLandingPage() {
  const location = useLocation();

  /**
   * React Router does not scroll on hash-only changes.
   * Retry until the element appears (handles cross-route deep links where the
   * page is still painting when the effect first fires).
   */
  useEffect(() => {
    if (location.pathname !== '/business') return;
    const id = location.hash?.replace(/^#/, '') ?? '';
    if (!id) return;

    let t: ReturnType<typeof window.setTimeout>;
    let attempts = 0;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (++attempts < 6) {
        t = window.setTimeout(tryScroll, 100);
      }
    };

    t = window.setTimeout(tryScroll, 80);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <div className={LANDING_PAGE_BG}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        <B2BLandingHeader />
        {/* Home */}
        <B2BHero />
        {/* Problem */}
        <B2BBeforeAfter />
        {/* Credibility after the pain point */}
        <B2BMetricsStrip />
        {/* Solution */}
        <B2BHowItWorks />
        {/* Who it's for */}
        <B2BWorksBestFor />
        {/* CTA */}
        <B2BFinalCTA />
      </motion.div>
      <B2BFooter />
    </div>
  );
}
